import {
	IListAttrs,
	IListColumnConfig,
	IListOperations,
} from '@datachain/ui-sdk/common';
import { castDraft, immerable, produce } from 'immer';
import { Map, Set } from 'immutable';

import { DepotItemEntity } from './depot-item.entity';

export interface IDepotListExtra extends IListColumnConfig<DepotItemEntity> {
	isFixed?: boolean;
	isSelectable?: boolean;
	isSearchable?: boolean;
	isInGroup?: boolean;
	isVisible?: boolean;
	canBeHidden?: boolean;
	canBeReordered?: boolean;
	canBeChosen?: boolean;
	format?: string;
	visibleIndex?: number;
	visible?: boolean;
	hasHeaderFilter?: boolean;
	width?: string | number;
	filterValue?: Array<string | number>;
	filterValues?: Array<string | number>;
	sortIndex?: number;
	sortOrder?: 'asc' | 'desc';
}

interface IDepotListEntity extends IListAttrs<DepotItemEntity> {
	availableColumns: Map<string, IDepotListExtra>;
}

const entityDefaults: IDepotListEntity = {
	elements: Map(),
	availableColumns: Map(),
};

export class DepotListEntity
	implements
		IDepotListEntity,
		IListOperations<DepotListEntity, DepotItemEntity>
{
	private [immerable] = true;
	public elements = entityDefaults.elements;
	public availableColumns = entityDefaults.availableColumns;

	public static build(availableCols: Array<IDepotListExtra>): DepotListEntity {
		const inst = new DepotListEntity();
		const cols = availableCols.reduce((acc, curr) => {
			acc.push([curr.field, curr]);
			return acc;
		}, new Array<[string, IDepotListExtra]>());
		inst.availableColumns = Map(cols);
		return inst;
	}

	private constructor() {}

	public getAvailableColumns(): Map<
		string,
		IListColumnConfig<DepotItemEntity>
	> {
		return this.availableColumns;
	}

	public setElements(elems: Map<string, DepotItemEntity>): DepotListEntity {
		return produce(this, (draft: DepotListEntity) => {
			draft.elements = elems;
		});
	}

	public extractUniqTags(...startValues: Array<string>): Set<string> {
		return this.elements.reduce((acc, depotItem) => {
			if (!depotItem.tags) {
				return acc;
			}
			return Set([
				...acc.toArray(),
				...depotItem.tags
					.toList()
					.toArray()
					.map((l) => l.label),
			]);
		}, Set<string>(startValues));
	}

	public extractModifiers(...startValues: Array<string>): Set<string> {
		return this.elements.reduce((acc, depotItem) => {
			if (depotItem.updatedByAvatar && depotItem.updatedByAvatar?.name) {
				acc = acc.add(depotItem.updatedByAvatar.name);
			}
			return acc;
		}, Set<string>(startValues));
	}

	public extractActionCreators(...startValues: Array<string>): Set<string> {
		return this.elements.reduce((acc, depotItem) => {
			if (depotItem.createdByAvatar && depotItem.createdByAvatar.name) {
				acc = acc.add(depotItem.createdByAvatar.name);
			}
			return acc;
		}, Set<string>(startValues));
	}

	public updateElements(updates: Array<[number, number]>): DepotListEntity {
		const updatedList = updates.reduce((list, curr) => {
			const [depotId, entitiesCount] = curr;
			const currentItem = list.get(`${depotId}`);
			if (!currentItem) {
				return list;
			}
			const updatedItem = currentItem.updateEntityCount(entitiesCount);
			return list.set(`${depotId}`, updatedItem);
		}, this.elements);
		return produce(this, (draft) => {
			draft.elements = castDraft(updatedList);
		});
	}
}
